import { useState } from 'react';
import { IconX } from '@tabler/icons-react';
import { cn } from '@/lib/utils';

/**
 * Free-text chips: type a value and press Enter (or comma) to add it; Backspace on an empty box
 * removes the last one. Chips look like MultiSelect's, for lists that have no fixed catalogue.
 *
 * value:     array of strings
 * max:       optional cap on the number of chips
 * maxLength: optional cap on one chip's length
 */
export default function TagInput({ value = [], onChange, placeholder = '', max, maxLength, disabled = false }) {
    const [draft, setDraft] = useState('');
    const tags = Array.isArray(value) ? value : [];
    const full = max != null && tags.length >= max;

    const commit = () => {
        const text = draft.replace(/\s+/g, ' ').trim();
        setDraft('');
        if (!text || full) return;
        if (tags.some((t) => t.toLowerCase() === text.toLowerCase())) return;
        onChange([...tags, text]);
    };
    const remove = (i) => onChange(tags.filter((_, j) => j !== i));

    return (
        <div
            className={cn(
                'w-full min-h-[38px] flex items-center gap-1.5 flex-wrap px-2 py-1.5 rounded-md border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 text-sm focus-within:ring-2 focus-within:ring-orange-500',
                disabled && 'opacity-50 cursor-not-allowed'
            )}
        >
            {tags.map((tag, i) => (
                <span key={tag} className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 text-xs">
                    {tag}
                    <button type="button" onClick={() => remove(i)} disabled={disabled} className="text-orange-500 hover:text-orange-700">
                        <IconX size={11} />
                    </button>
                </span>
            ))}
            {!full && (
                <input
                    type="text"
                    value={draft}
                    maxLength={maxLength}
                    disabled={disabled}
                    placeholder={tags.length ? '' : placeholder}
                    onChange={(e) => setDraft(e.target.value)}
                    onBlur={commit}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ',') {
                            e.preventDefault();
                            commit();
                        } else if (e.key === 'Backspace' && !draft && tags.length) {
                            remove(tags.length - 1);
                        }
                    }}
                    className="flex-1 min-w-[8rem] px-1 py-0.5 bg-transparent outline-none text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400"
                />
            )}
        </div>
    );
}
